import React, { useContext, useMemo, useState } from 'react';

import { Alert } from 'antd';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import styled from 'styled-components';

import { getCustomField, getCustomFieldInitialValue } from '@libs/NwCustomFields';
import { toISODate } from '@libs/NwMoment';
import { checkTenant } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';

import { useAddNewInvoice } from '@hooks/API/UseInvoices';
import useDepartments from '@hooks/CachedLists/UseDepartments';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import {
    NWInput,
    NWPaymentTermSelector,
    NWBankAccountsSelector,
    NWSwitch,
    NWSelect,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import StyleVariables from '@components/Gui/StyleVariables.js';

const InvoiceContent = styled.div`
    .contact-name {
        font-weight: bold;
        font-size: ${(props) => props.theme.FontSizeXLg};
        margin-bottom: 2rem;
    }

    .bold-text {
        font-weight: bold;
    }

    .statements-content {
        margin-bottom: 2rem;
        .contact-row {
            display: flex;
            flex-direction: row;
            font-size: ${(props) => props.theme.FontSizeSm};
            .select-all {
                color: ${StyleVariables.PrimaryColor};
                margin-left: 1rem;
                cursor: pointer;
                user-select: none;
            }
        }
        .tx-details {
            font-size: ${(props) => props.theme.FontSizeSm};
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            .ant-checkbox-wrapper {
                font-size: ${(props) => props.theme.FontSizeSm};
            }
        }
    }
`;
const CreateInvoiceDrawer = ({ onClose, data, onReload, onInvoiceCreated, projectCustomFields }) => {
    const { cachedList } = useContext(ListContext);
    const [touchForm, setTouchForm] = useState(false);
    const { mutateAsync: onAddNewInvoice } = useAddNewInvoice();
    const [errorCreating, setErrorCreating] = useState(null);
    const departments = useDepartments();

    const departmentsInStatements = useMemo(() => {
        const departments = new Set();
        data.SelectedStatementObjects.forEach((item) => {
            if (item.DepartmentCreated) {
                departments.add(item.DepartmentCreated);
            }
        });
        return Array.from(departments);
    }, [data.SelectedStatementObjects]);

    const departmentsOptions = useMemo(() => {
        if (departmentsInStatements.length > 1) {
            const options = [
                {
                    label: 'Select a department',
                    value: '',
                },
                ...departments
                    .filter((department) => departmentsInStatements.includes(department.id))
                    .map((department) => ({
                        label: department.name,
                        value: department.id,
                    })),
            ];
            return options;
        }
        return null;
    }, [departmentsInStatements, departments]);

    const configurations = useConfigurations();

    const schemas = [];
    if (configurations && configurations.COA) {
        const COAjs = JSON.parse(configurations.COA);
        if (COAjs.schemas && COAjs.schemas.length > 0) {
            schemas.push(...COAjs.schemas);
        }
    }
    const enableCOA = schemas.length > 0;

    //get all custom fields
    const customFields = cachedList.customFields.items['Invoice'];
    const availableCustomFields = customFields.filter((field) => !field.Disabled);

    let clientHasDefaultPaymentTerm = false;

    if (
        !cachedList ||
        !cachedList.paymentTerms ||
        !cachedList.paymentTerms.items ||
        cachedList.paymentTerms.items.length === 0 ||
        !cachedList.bankAccounts ||
        !cachedList.bankAccounts.items ||
        cachedList.bankAccounts.items.length === 0
    ) {
        return (
            <h1>
                Before creating your first invoice, please make sure that in the "settings" area you have configured at
                least a payment term and a bank account
            </h1>
        );
    }

    let initialPaymentTerm = cachedList.paymentTerms.items.find((el) => el.IsDefault);
    let initialBankAccount = cachedList.bankAccounts.items.find((el) => el.IsDefault);
    //if no default payment term or bank account, select the first one
    if (!initialPaymentTerm) {
        //TODO: check if the billing info has a default payment term
        initialPaymentTerm = cachedList.paymentTerms.items[0];
    }
    if (!initialBankAccount) {
        initialBankAccount = cachedList.bankAccounts.items[0];
    }
    //if no default payment term or bank account return error message
    if (!initialPaymentTerm || !initialBankAccount) {
        return (
            <h1>
                Before creating your first invoice, please make sure that in the "settings" area you have configured at
                least a payment term and a bank account
            </h1>
        );
    }

    if (data.BillingInfoDefaultPaymentTermCode) {
        initialPaymentTerm = cachedList.paymentTerms.items.find(
            (el) => el.Code === data.BillingInfoDefaultPaymentTermCode
        );
        clientHasDefaultPaymentTerm = true;
    }

    let initialCustomFieldsValues = {};
    availableCustomFields.map((customField) => {
        let value = null;
        if (projectCustomFields && projectCustomFields[customField.Name]) {
            value = projectCustomFields[customField.Name];
        }
        if (!customField.Disabled) {
            initialCustomFieldsValues = {
                ...initialCustomFieldsValues,
                [`CF_${customField.Name}`]: getCustomFieldInitialValue(customField.TypeName, value),
            };
        }
        return null;
    });

    const groupCommissionsDefault = checkTenant('elitemodels') ? false : true;

    const initialValues = {
        InvoiceName: data.ProjectName ? `Invoice for ${data.ProjectName}` : 'New Invoice',
        PaymentTermCode: initialPaymentTerm.Code,
        BankAccountID: initialBankAccount.ID,
        COASchema: null,
        DepartmentCreated: departmentsInStatements.length === 1 ? departmentsInStatements[0] : '',
        GroupCommissions: (data.AgencyFeeCount && data.AgencyFeeCount) > 1 ? groupCommissionsDefault : false,
        ...initialCustomFieldsValues,
    };

    const saveInvoice = async (values, setSubmitting) => {
        setSubmitting(true);
        try {
            let customFieldsData = {};
            Object.keys(values).map((key) => {
                if (key.startsWith('CF_')) {
                    const newKey = key.replace('CF_', '');
                    if (values[key] !== '' && JSON.stringify(values[key]) !== JSON.stringify([]) && values[key]) {
                        const customField = availableCustomFields.find((item) => item.Name === newKey);
                        if (customField.TypeName === 'MultipleChoice') {
                            customFieldsData = {
                                ...customFieldsData,
                                [newKey]: values[key].join(','),
                            };
                        } else if (customField.TypeName === 'Date') {
                            customFieldsData = {
                                ...customFieldsData,
                                [newKey]: toISODate(values[key]),
                            };
                        } else {
                            customFieldsData = {
                                ...customFieldsData,
                                [newKey]: values[key],
                            };
                        }
                    }
                }
                return null;
            });

            const StatementRows = data.SelectedStatementObjects.map((item) => {
                return { StatementID: item.ID, AccountCode: '' };
            });

            if (enableCOA && values.COASchema) {
                const selectedSchema = schemas.find((schema) => schema.code === values.COASchema);
                if (selectedSchema) {
                    StatementRows.forEach((item) => {
                        //get the statement info to map the account code
                        const statement = data.SelectedStatementObjects.find(
                            (statement) => statement.ID === item.StatementID
                        );
                        item.AccountCode = selectedSchema.defaultAccountCode;
                        if (statement && selectedSchema.overrides && selectedSchema.overrides.length > 0) {
                            selectedSchema.overrides.forEach((override) => {
                                if (override.family === statement.Family) {
                                    if (typeof override.lineType === 'undefined') {
                                        item.AccountCode = override.accountCode;
                                    } else {
                                        if (override.lineType === 'commission' && statement.isCommission) {
                                            item.AccountCode = override.accountCode;
                                        }
                                        if (override.lineType === 'advance' && statement.isAdvance) {
                                            item.AccountCode = override.accountCode;
                                        }
                                        if (
                                            override.lineType === 'standard' &&
                                            !statement.isAdvance &&
                                            !statement.isCommission
                                        ) {
                                            item.AccountCode = override.accountCode;
                                        }
                                    }
                                }
                            });
                        }
                    });
                }
            }

            const invoiceDataToPost = {
                ContactID: data.ContactID,
                BillingInfoID: data.BillingInfoID,
                Name: values.InvoiceName,
                PaymentTermCode: values.PaymentTermCode,
                BankAccountID: values.BankAccountID,
                Type: data.Type,
                Currency: data.Currency,
                StatementRows: StatementRows,
                GroupCommissions: values.GroupCommissions,
                CustomFields: customFieldsData,
                DepartmentCreated: values.DepartmentCreated,
            };

            const invoiceId = await onAddNewInvoice({ data: invoiceDataToPost });
            if (onInvoiceCreated) {
                onInvoiceCreated(invoiceId);
            }
            if (onReload) {
                onReload();
            }
            onClose();
            setSubmitting(false);
        } catch (e) {
            setErrorCreating(`Error creating invoice: ${e.message}`);
            setSubmitting(false);
        }
    };

    const writeTerm = (term) => {
        let stringTerms = term.Code;
        if (term.Name !== term.Code) {
            stringTerms = `${stringTerms} - ${term.Name}`;
        }
        return stringTerms;
    };

    return (
        <NwDrawer touchForm={touchForm} onTouchForm={setTouchForm} onClose={onClose} title="Create Invoice">
            <InvoiceContent>
                <h3>Invoice Currency: {data.Currency}</h3>
                <p>
                    Client default payment terms: {clientHasDefaultPaymentTerm ? writeTerm(initialPaymentTerm) : 'n.d.'}
                </p>
                <br />
                <Formik
                    initialValues={initialValues}
                    onSubmit={(values, { setSubmitting }) => {
                        saveInvoice(values, setSubmitting);
                    }}
                    validationSchema={Yup.object().shape({
                        InvoiceName: Yup.string().required('Title is required'),
                        DepartmentCreated: departmentsOptions
                            ? Yup.string().required('Department is required').min(1, 'Department is required')
                            : Yup.string(),
                    })}
                >
                    {({ isSubmitting, values, handleSubmit }) => (
                        <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit}>
                            {departmentsOptions && (
                                <Field
                                    component={NWSelect}
                                    label="Department"
                                    name="DepartmentCreated"
                                    options={departmentsOptions}
                                    value={values.DepartmentCreated}
                                    extra="Please select a department for this invoice. The invoice includes items from multiple departments."
                                />
                            )}
                            <Field
                                component={NWPaymentTermSelector}
                                label="Payment Terms"
                                name="PaymentTermCode"
                                value={values.PaymentTermCode}
                            />
                            <Field
                                component={NWBankAccountsSelector}
                                label="Bank Account"
                                name="BankAccountID"
                                value={values.BankAccountID}
                            />
                            <Field
                                component={NWInput}
                                label="Invoice Description"
                                name="InvoiceName"
                                maxLength={50}
                                type="text"
                                value={values.InvoiceName}
                            />
                            {data.AgencyFeeCount && data.AgencyFeeCount > 1 ? (
                                <Field
                                    component={NWSwitch}
                                    label=""
                                    name="GroupCommissions"
                                    text="group agency fees in a single line"
                                    value={values.GroupCommissions}
                                />
                            ) : null}
                            {errorCreating && (
                                <Alert style={{ margin: '1rem 0' }} message={errorCreating} type="error" />
                            )}
                            {enableCOA && (
                                <>
                                    <br />
                                    <NwDivider>Chart of Account</NwDivider>
                                    <br />
                                    <Field
                                        component={NWSelect}
                                        label="Account Schema"
                                        name="COASchema"
                                        options={schemas.map((schema) => ({ label: schema.code, value: schema.code }))}
                                        value={values.COASchema}
                                    />
                                </>
                            )}
                            <br />
                            {availableCustomFields.length > 0 && (
                                <>
                                    <NwDivider>Additional Info</NwDivider>
                                    <br />
                                    {availableCustomFields.map((customField, index) => {
                                        return (
                                            <div key={`${customField.Name}-${index}`}>
                                                {getCustomField(values, customField, 'CF_')}
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                            <NwFormButtonsBar
                                marginTop="4rem"
                                left={
                                    <NwCancelButton
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            setTouchForm(false);
                                            onClose();
                                        }}
                                    />
                                }
                                right={
                                    <NwSaveButton
                                        htmlType="submit"
                                        loading={isSubmitting}
                                        //disabled={currencyValidation()}
                                    />
                                }
                            />
                        </NwForm>
                    )}
                </Formik>
            </InvoiceContent>
        </NwDrawer>
    );
};

export default CreateInvoiceDrawer;
